import React from "react";
import { AvatarRing } from "../../../../common/comms/AvatarRing";
import { StatusBadge } from "../../../../common/comms/StatusBadge";
import { useCommsIdentity } from "../../../../hooks/comms/useCommsIdentity";
import { relativeTime } from "../../../../utils/oversightFormat";
import { daysSinceActivity, isNeverMessaged, QUIET_AFTER_DAYS } from "./clientRosterSort";

/**
 * Map a client roster status to a StatusBadge tone.
 * @param {string|null|undefined} status
 * @returns {'success'|'internal'|'neutral'}
 */
const statusTone = (status) => {
  const raw = (status || "").toLowerCase();
  if (raw === "active") return "success";
  if (raw === "paused") return "internal";
  return "neutral";
};

/** Title-case a status for display, falling back to "Unknown". */
const statusLabel = (status) => {
  if (!status) return "Unknown";
  return status.charAt(0).toUpperCase() + status.slice(1);
};

/**
 * Silence tone: red once a room has been quiet twice the window, amber past the
 * window, plain otherwise. Never-messaged is red regardless of age.
 * @param {Object} client
 * @param {number|null} days
 */
function silenceClass(client, days) {
  if (isNeverMessaged(client)) return "text-danger-dark";
  if (days == null) return "text-gray-400";
  if (days >= QUIET_AFTER_DAYS * 2) return "text-danger-dark";
  if (days >= QUIET_AFTER_DAYS) return "text-internal-text";
  return "text-gray-400";
}

/**
 * A single row in a coach's client roster.
 *
 * Stacks to a card under `sm`; the numeric columns move onto the second line
 * rather than being dropped, so a phone shows the same facts as a desktop.
 *
 * @param {Object} props
 * @param {Object} props.client - `{ client_id, room_id, display_id, status,
 *   frozen_at, frozen_reason, last_activity_at, message_count, avg_csat }`.
 * @param {(roomId: string) => void} props.onOpenRoom - Navigate to the room (read-only).
 */
export function ClientTableRow({ client, onOpenRoom }) {
  const { getClientName } = useCommsIdentity();

  const name =
    getClientName(client.client_id) ||
    (client.display_id != null ? `#${client.display_id}` : null) ||
    (client.client_id != null ? `#${String(client.client_id).slice(-6)}` : null) ||
    "Client";
  const colorIndex = client.client_id != null ? String(client.client_id).charCodeAt(0) % 8 : 0;
  const roomId = client.room_id;
  const csat = client.avg_csat != null ? client.avg_csat.toFixed(1) : "—";
  const messageCount = client.message_count ?? 0;
  // "Heard from their coach" is NOT last activity: a client who messages daily
  // and is never answered has recent activity and has heard nothing.
  const silentDays = daysSinceActivity(client.last_coach_message_at ?? client.last_activity_at);
  const never = isNeverMessaged(client);
  const foodDays = client.food_log_days;
  const trackingWindow = client.tracking_window_days ?? 7;

  // "F · 34y · with them 62d" — only the parts the payload actually carries.
  const identity = [
    client.sex ? String(client.sex).charAt(0).toUpperCase() : null,
    client.age != null ? `${client.age}y` : null,
    client.days_with_coach != null ? `with them ${client.days_with_coach}d` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="flex flex-wrap sm:flex-nowrap items-center gap-x-3 gap-y-1.5 px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0">
      <AvatarRing name={name} colorIndex={colorIndex} size="sm" />

      <div className="min-w-0 flex-1 order-1">
        <p className="text-sm font-medium text-gray-800 truncate">{name}</p>
        {identity && <p className="text-[11px] text-gray-400 truncate">{identity}</p>}
        <p className={`text-[11px] truncate ${silenceClass(client, silentDays)}`}>
          {never
            ? "Never messaged"
            : client.last_coach_message_at
            ? `Coach wrote ${relativeTime(client.last_coach_message_at)}`
            : `Last activity ${relativeTime(client.last_activity_at)}`}
        </p>
      </div>

      <div className="hidden lg:block w-16 text-right flex-shrink-0 order-3">
        <p
          className={`text-sm font-semibold ${
            foodDays == null
              ? "text-gray-300"
              : foodDays === 0
              ? "text-danger-dark"
              : foodDays >= 5
              ? "text-success"
              : "text-internal-text"
          }`}
        >
          {foodDays == null ? "—" : foodDays}
          {foodDays != null && (
            <span className="text-[10px] font-normal text-gray-400">/{trackingWindow}d</span>
          )}
        </p>
        <p className="text-[10px] uppercase tracking-wide text-gray-400">Food</p>
      </div>

      <div className="w-14 text-right flex-shrink-0 order-4 sm:order-2">
        <p className="text-sm font-semibold text-gray-700">{messageCount}</p>
        <p className="text-[10px] uppercase tracking-wide text-gray-400">Msgs</p>
      </div>

      <div className="w-14 text-right flex-shrink-0 order-5 sm:order-3">
        <p className="text-sm font-semibold text-gray-700">{csat}</p>
        <p className="text-[10px] uppercase tracking-wide text-gray-400">CSAT</p>
      </div>

      <div className="flex items-center gap-1.5 flex-shrink-0 order-2 sm:order-4">
        {client.awaiting_coach_reply && (
          <span
            title="The newest message in this room is the client's"
            className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-danger-bg text-danger-dark whitespace-nowrap"
          >
            Awaiting reply
          </span>
        )}
        <StatusBadge tone={statusTone(client.status)} dot>
          {statusLabel(client.status)}
        </StatusBadge>
        {/* The API has always sent these two; the old row dropped them, so a
            frozen room looked identical to a live one. */}
        {client.frozen_at && (
          <span title={client.frozen_reason || "Frozen"} className="inline-flex">
            <StatusBadge tone="danger">Frozen</StatusBadge>
          </span>
        )}
      </div>

      <button
        type="button"
        onClick={() => roomId && onOpenRoom(roomId)}
        disabled={!roomId}
        className="min-h-[44px] inline-flex items-center flex-shrink-0 text-[11px] sm:text-xs font-semibold text-orange-primary hover:text-orange-600 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap order-6"
      >
        Open room (read-only)
      </button>
    </div>
  );
}

export default ClientTableRow;
